import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Box, Fade, Flex, Image, SlideFade, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { Prize } from 'models';
import routes from 'routes';
import { createLava } from 'utils/createLavaBubles';

const PrizeBanner = ({ prizes }: { prizes: Prize[] }) => {
  const [activePrize, setActivePrize] = useState<Prize | null>(null);
  const [isVisible, setIsVisible] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  const validPrizes = useMemo(
    () => prizes.filter((prize): prize is Prize => prize !== null && prize !== undefined && prize.isActive),
    [prizes]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      createLava('bubble');
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (validPrizes.length === 0) return;

    if (!activePrize || !validPrizes.find((prize) => prize.id === activePrize.id)) {
      setActivePrize(validPrizes[0]);
    }
  }, [validPrizes, activePrize]);

  useEffect(() => {
    if (validPrizes.length <= 1) return;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      setIsVisible(false);

      setTimeout(() => {
        setActivePrize((prev) => {
          if (!prev) return validPrizes[0];
          const currentIndex = validPrizes.findIndex((prize) => prize.id === prev.id);
          const nextIndex = (currentIndex + 1) % validPrizes.length;
          return validPrizes[nextIndex];
        });
        setIsVisible(true);
      }, 300);
    }, 4000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [validPrizes]);

  return (
    <Flex px="15px" py="12px" flexDirection="column" gap="8px">
      <Flex
        w="100%"
        h="130px"
        backgroundColor="#F83C3C"
        borderRadius="8px"
        flexDirection="column"
        justify="space-between"
        p="10px 12px"
        position="relative"
        overflow="hidden"
        onClick={() => {
          navigate(`${routes.leaderboard}/monthly/${dayjs().format('YYYY-MM-DD')}?friends=false&league=all`);
        }}
      >
        <SlideFade
          in={isVisible}
          offsetY="20px"
          transition={{ enter: { duration: 0.5 }, exit: { duration: 0.3 } }}
          key={activePrize?.id}
          style={{
            position: 'absolute',
            top: '-10px',
            right: '-30px',
            zIndex: '4',
          }}
        >
          <Image
            src={activePrize?.image || '/promo-hoodie.webp'}
            alt={activePrize?.title || 'banner'}
            w="220px"
            h="220px"
            objectPosition="center"
            objectFit="contain"
          />
        </SlideFade>
        <canvas
          id="bubble"
          style={{
            position: 'absolute',
            zIndex: 1,
            top: '-10px',
            left: '-10px',
            width: 'calc(100% + 20px)',
            height: 'calc(100% + 20px)',
            filter: 'blur(9px)',
          }}
        />

        <Text
          fontSize="20px"
          fontWeight="400"
          lineHeight="1.1"
          textTransform="uppercase"
          color="#fff"
          fontFamily="Antonio"
          zIndex="3"
          maxW="calc(100% - 150px)"
        >
          <Fade
            in={isVisible}
            transition={{ enter: { duration: 0.5, ease: 'easeInOut' }, exit: { duration: 0.3, ease: 'easeInOut' } }}
            key={`text-${activePrize?.id}`}
            style={{ display: 'inline-block' }}
          >
            {activePrize?.title}
          </Fade>
        </Text>

        <Text
          fontSize="11px"
          fontWeight="400"
          lineHeight="1.3"
          textTransform="uppercase"
          color="#fff"
          fontFamily="Special Gothic Expanded One"
          zIndex="3"
          display="flex"
          alignItems="center"
          gap="1px"
        >
          LEADERBOARD{' '}
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M8 4C8 4.09285 7.95841 4.17193 7.88328 4.24175L4.25125 7.40318C4.18374 7.46774 4.09212 7.5 3.98886 7.5C3.78371 7.5 3.625 7.35997 3.625 7.17154C3.625 7.07997 3.66478 7.00091 3.73048 6.93797L7.10332 4L3.73048 1.06205C3.66478 0.999114 3.625 0.916411 3.625 0.82845C3.625 0.640013 3.78371 0.5 3.98886 0.5C4.09212 0.5 4.18374 0.532282 4.25125 0.596847L7.88328 3.75825C7.95841 3.82807 8 3.90715 8 4Z"
              fill="white"
              fillOpacity="0.85"
            />
          </svg>
        </Text>
      </Flex>
      <Flex
        fontSize="10px"
        fontWeight="500"
        lineHeight="1.3"
        zIndex="3"
        alignItems="center"
        justifyContent="center"
        gap="1px"
        color="rgba(255, 255, 255, 0.4)"
      >
        <Box as={Link} to={activePrize?.rules || ''} target="_blank" textDecoration="underline">
          Rules apply.
        </Box>{' '}
        US shipping only.
      </Flex>
    </Flex>
  );
};

export default PrizeBanner;
